import type { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";
import { useGetDepartmentTimeOffRequests } from "../../components/RequestTimeOff/timeOffRequestsHooks";
import { useAnalytics } from "../../contexts/analytics/hooks";
import { useHospitalData } from "../../contexts/HospitalDataProvider";
import { useDrawerTriggerNotification, useRecommendationsDrawerContext } from "../../contexts/RecommendationsDrawerContext";
import { useWeekIndex } from "../../contexts/WeekIndexProvider";
import { type DepartmentType, RecommendationOpenSource } from "../../utils/models";
import { buildSynergyGridData, type SynergyGridData } from "./utils";

export const useSynergyStaffingState = () => {
	const { hospitalData, chosenDepartment } = useHospitalData();
	const { track } = useAnalytics();
	const { isOpen: drawerOpen, toggleDrawerOpen } = useRecommendationsDrawerContext();
	const { getDrawerTriggerNotification } = useDrawerTriggerNotification();
	// SynergyModeStaffing has no open-shift request data wired in today — badge reflects chat-unread only.
	const notification = getDrawerTriggerNotification(0);
	const [selectedTypes, setSelectedTypes] = useState<(DepartmentType | "all")[]>(["all"]);
	const { weekIndex } = useWeekIndex();
	const { data: timeOffRequests = [], refetch: refetchDayOffRequests, isFetching: refetchDayOffRequestsLoading } = useGetDepartmentTimeOffRequests();

	const currentWeekData = useMemo(() => hospitalData.weeks[weekIndex], [hospitalData.weeks, weekIndex]);
	const gridData: SynergyGridData = useMemo(
		() => buildSynergyGridData(chosenDepartment.locations, currentWeekData, selectedTypes),
		[chosenDepartment.locations, currentWeekData, selectedTypes],
	);

	// Get week start and end dates for header
	const weekStartDate: DateTime | undefined = useMemo(() => currentWeekData?.weeklyShifts[0]?.days[0]?.date, [currentWeekData]);

	const [collapsedSections, setCollapsedSections] = useState<string[]>([]);

	const handleSectionCollapseToggle = useCallback((section: string) => {
		setCollapsedSections((prev) => (prev.includes(section) ? prev.filter((s) => s !== section) : [...prev, section]));
	}, []);

	const handleDepartmentFilterChange = (selected: (DepartmentType | "all")[]) => {
		setSelectedTypes(selected);
	};

	const handleToggleDrawer = () => {
		track(!drawerOpen ? "synergy_staffing_recommendations_drawer_opened" : "synergy_staffing_recommendations_drawer_closed");
		toggleDrawerOpen(!drawerOpen, RecommendationOpenSource.WeeklyView);
	};

	const handleRefetchDayOffRequests = async () => {
		await refetchDayOffRequests();
	};

	return {
		gridData,
		weekStartDate,
		collapsedSections,
		handleSectionCollapseToggle,
		chosenDepartment,
		handleDepartmentFilterChange,
		drawerOpen,
		handleToggleDrawer,
		notification,
		timeOffRequests,
		handleRefetchDayOffRequests,
		refetchDayOffRequestsLoading,
	};
};
